import React, { useContext, useEffect, useMemo, useRef } from 'react';
import axiosInstance from '../../axios/axiosinstance';
import { AuthContext } from '../../context/logeduser';

const DEFAULT_WIDGET_URL = 'http://localhost:3000/widget.js';
const DEFAULT_CHAT_API_URL = 'http://localhost:3000/api/chat';
const DEFAULT_SUPPORT_API_URL = 'http://localhost:3000/api/support';
const WIDGET_SCRIPT_ID = 'luma-chat-widget-script';
const SUPPORT_ELEMENT_NAME = 'luma-chat-widget';
const SUPPORT_CATEGORIES = new Set(['PAYROLL', 'LEAVE', 'DOCUMENTS', 'IT', 'GENERAL']);
const MULTIPART_CONFIG = { headers: { 'Content-Type': undefined } };

const cleanText = (value, maxLength = 10000) =>
  String(value || '').trim().slice(0, maxLength);

const apiError = (error, fallback) => {
  const body = error?.response?.data;
  const message = body?.error?.message || body?.error || body?.detail || body?.message;
  return new Error(cleanText(message || fallback, 300));
};

const normalizeHr = (raw = {}) => ({
  id: raw.id,
  name: cleanText(raw.name || 'HR', 150),
  branch: cleanText(raw.branch, 150),
  location: cleanText(raw.location, 150),
});

const normalizeMessage = (raw = {}) => ({
  id: String(raw.id || raw.messageId || ''),
  role: raw.is_from_hr ? 'support' : (raw.role || raw.sender || 'user'),
  content: cleanText(raw.body || raw.content || raw.text),
  seen: typeof raw.seen === 'boolean' ? raw.seen : null,
  attachment_url: cleanText(raw.attachment_url || raw.attachmentUrl, 4000),
  attachment_name: cleanText(raw.attachment_name || raw.attachmentName, 255),
  createdAt: raw.created_at || raw.createdAt || raw.timestamp,
  senderName: cleanText(raw.author_name || raw.senderName, 150),
});

const normalizedStatus = (ticket, messages) => {
  const status = String(ticket.status || '').toUpperCase();
  if (status === 'RESOLVED') return 'resolved';
  if (status === 'ANSWERED' || status === 'AWAITING_EMPLOYEE') return 'human_active';
  if (messages.some((message) => message.role === 'support')) return 'human_active';
  if (ticket.assigned_hr_id || ticket.assigned_hr?.id || ticket.assignedHr?.id) return 'agent_assigned';
  return 'queued_for_agent';
};

const normalizeTicket = (response = {}) => {
  const ticket = response.ticket || response;
  const messages = Array.isArray(ticket.messages)
    ? ticket.messages.map(normalizeMessage).filter((message) => message.content || message.attachment_url)
    : [];
  const assigned = ticket.assignedHr || ticket.assigned_hr || {
    id: ticket.assigned_hr_id,
    name: ticket.assigned_hr_name,
    branch: ticket.assigned_hr_branch,
    location: ticket.assigned_hr_location,
  };
  const assignedHr = assigned?.id ? normalizeHr(assigned) : null;

  return {
    id: String(ticket.id || ticket.ticketId || ''),
    status: normalizedStatus(ticket, messages),
    category: cleanText(ticket.category, 30),
    department: cleanText(ticket.department || ticket.category, 30).toLowerCase(),
    assignedHr,
    assignedAgent: assignedHr,
    messages,
    resolution: cleanText(ticket.resolution, 2000),
    callStatus: ticket.callStatus || ticket.call_status,
  };
};

const inferCategory = (payload = {}) => {
  const requested = String(payload.category || '').toUpperCase();
  if (SUPPORT_CATEGORIES.has(requested)) return requested;

  const text = (payload.transcript || [])
    .map((message) => message?.content || '')
    .join(' ');
  if (/\b(salary|payroll|payslip|deduction|lop|tax|reimbursement|allowance)\b/i.test(text)) return 'PAYROLL';
  if (/\b(leave|attendance|absent|holiday|shift|check[ -]?(?:in|out))\b/i.test(text)) return 'LEAVE';
  if (/\b(form\s*16|document|letter|certificate|proof)\b/i.test(text)) return 'DOCUMENTS';
  if (/\b(it|login|password|access|laptop|device|software|email)\b/i.test(text)) return 'IT';
  return 'GENERAL';
};

const createPortalSupportAdapter = () => {
  const fallbackHandoffIds = new Map();

  const clientHandoffId = (payload, transcript) => {
    if (payload.clientHandoffId) return cleanText(payload.clientHandoffId, 150);
    const fingerprint = JSON.stringify(transcript);
    if (!fallbackHandoffIds.has(fingerprint)) {
      const randomPart = window.crypto?.randomUUID?.()
        || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      fallbackHandoffIds.set(fingerprint, `hr-widget-${randomPart}`);
    }
    return fallbackHandoffIds.get(fingerprint);
  };

  return {
    async listAgents() {
      try {
        const { data } = await axiosInstance.get('api/support/hr-options/');
        const hrs = Array.isArray(data) ? data : (data?.hrs || data?.results || []);
        return hrs.map(normalizeHr).filter((hr) => hr.id && hr.name);
      } catch (error) {
        throw apiError(error, 'Could not load the HR list. Please sign in and try again.');
      }
    },

    async createTicket(payload = {}) {
      const selectedHrId = Number(payload.selectedHrId);
      if (!Number.isInteger(selectedHrId) || selectedHrId <= 0) {
        throw new Error('Choose an HR before sending your request.');
      }
      const transcript = (Array.isArray(payload.transcript) ? payload.transcript : [])
        .filter((message) => ['user', 'assistant'].includes(message?.role) && message?.content)
        .slice(-50)
        .map((message) => ({ role: message.role, content: cleanText(message.content) }));

      try {
        const { data } = await axiosInstance.post('api/support/ai-handoffs/', {
          selected_hr_id: selectedHrId,
          category: inferCategory({ ...payload, transcript }),
          escalation_reason: cleanText(payload.escalationReason, 100),
          client_handoff_id: clientHandoffId(payload, transcript),
          transcript,
        });
        return normalizeTicket(data);
      } catch (error) {
        throw apiError(error, 'Could not send your request to HR. Please try again.');
      }
    },

    async getTicket(ticketId) {
      try {
        const { data } = await axiosInstance.get(
          `api/support/tickets/${encodeURIComponent(ticketId)}/`
        );
        return normalizeTicket(data);
      } catch (error) {
        throw apiError(error, 'Could not refresh the HR conversation.');
      }
    },

    async sendMessage(ticketId, content, file) {
      const message = cleanText(content, 4000);
      if (!message && !file) throw new Error('Message or attachment is required.');
      try {
        let body = { message };
        if (file) {
          const formData = new FormData();
          formData.append('message', message);
          formData.append('attachment', file);
          body = formData;
        }

        const { data } = await axiosInstance.post(
          `api/support/tickets/${encodeURIComponent(ticketId)}/messages/`,
          body,
          file ? MULTIPART_CONFIG : undefined
        );
        return normalizeTicket(data);
      } catch (error) {
        throw apiError(error, 'Could not send your message to HR.');
      }
    },

    async markRead(ticketId) {
      try {
        await axiosInstance.post(
          `api/support/tickets/${encodeURIComponent(ticketId)}/mark-read/`
        );
      } catch (error) {
        throw apiError(error, 'Could not update the conversation read status.');
      }
    },
  };
};

const signInRequired = async () => {
  throw new Error('Sign in as an employee before contacting HR support.');
};

const GUEST_SUPPORT_ADAPTER = Object.freeze({
  listAgents: signInRequired,
  createTicket: signInRequired,
  getTicket: signInRequired,
  sendMessage: signInRequired,
  markRead: signInRequired,
});

export default function AiHrAssistant({ publicGuide = false }) {
  const auth = useContext(AuthContext);
  const authData = auth?.authData;
  const widgetRef = useRef(null);
  const widgetUrl = process.env.REACT_APP_LUMA_WIDGET_URL || DEFAULT_WIDGET_URL;
  const isEmployeeSession = Boolean(!publicGuide && authData?.employee_no && !authData?.admin);
  const storageScope = publicGuide
    ? 'public-hr-portal-guide'
    : (isEmployeeSession ? String(authData.employee_no) : 'guest');
  const supportAdapter = useMemo(
    () => {
      if (publicGuide) return null;
      return isEmployeeSession && storageScope !== 'guest'
        ? createPortalSupportAdapter()
        : GUEST_SUPPORT_ADAPTER;
    },
    [isEmployeeSession, publicGuide, storageScope]
  );

  useEffect(() => {
    if (window.customElements?.get(SUPPORT_ELEMENT_NAME)) {
      return undefined;
    }

    if (document.getElementById(WIDGET_SCRIPT_ID)) {
      return undefined;
    }

    const script = document.createElement('script');
    script.id = WIDGET_SCRIPT_ID;
    script.src = widgetUrl;
    script.async = true;
    document.head.appendChild(script);

    // Keep the shared script mounted when React routes change. Removing it can
    // interrupt an active support conversation in another mounted widget.
    return undefined;
  }, [widgetUrl]);

  useEffect(() => {
    if (!supportAdapter) return undefined;
    const widgetElement = widgetRef.current;
    let cancelled = false;

    const install = () => {
      if (!cancelled && widgetElement) {
        // This is an in-memory property, never an HTML attribute. The adapter
        // uses the portal's authenticated axios client, so no JWT is exposed to
        // the custom element or copied into its localStorage history.
        widgetRef.current.supportAdapter = supportAdapter;
      }
    };

    if (window.customElements?.get(SUPPORT_ELEMENT_NAME)) install();
    else window.customElements?.whenDefined(SUPPORT_ELEMENT_NAME).then(install);

    return () => {
      cancelled = true;
      if (widgetElement?.supportAdapter === supportAdapter) {
        widgetElement.supportAdapter = null;
      }
    };
  }, [supportAdapter]);

  return React.createElement(SUPPORT_ELEMENT_NAME, {
    ref: widgetRef,
    id: publicGuide ? 'hr-portal-guide' : 'hr-ai-assistant',
    'storage-scope': storageScope,
    'agent-name': 'Aira',
    'agent-role': publicGuide ? 'HR Portal guide' : 'HR AI assistant',
    greeting: publicGuide
      ? "Hi! I'm Aira, your HR Portal guide. I can explain the portal's features, HR modules, employee services, security, and login process."
      : "Hi! I'm Aira, your HR assistant. I can help with leave, attendance, payroll, payslips, Form 16, documents, and company policies.",
    'accent-color': '#7c5cff',
    'launcher-label': publicGuide ? 'Explore HR Portal' : 'Ask HR AI',
    'quick-actions': publicGuide
      ? 'Explore HR Portal features|What can employees do?|What can HR and admins do?|How is portal data secured?'
      : 'Leave or attendance question|Payroll or payslip issue',
    'human-action-label': publicGuide ? undefined : 'Talk to HR support',
    'assistant-mode': publicGuide ? 'hr-portal-guide' : undefined,
    'disable-human-support': publicGuide ? '' : undefined,
    'knowledge-threshold': '0.65',
    'api-url':
      process.env.REACT_APP_LUMA_CHAT_API_URL || DEFAULT_CHAT_API_URL,
    'support-api-url':
      process.env.REACT_APP_LUMA_SUPPORT_API_URL || DEFAULT_SUPPORT_API_URL,
  });
}
